import { createSelector } from 'reselect'

import { getUser } from 'common'

/**
 * @deprecated Use `useGetUserQuery` from 'common' instead
 *
 * Retrieves the data from the getUser selector within the Redux state
 * @param {object} state - the selected state
 * @returns {object} the current user object or an empty object if no data
 */
const currentUserSelector = (state) => getUser.select()(state)?.data || {}

export const authNameSelector = createSelector(
  currentUserSelector,
  (user) => user.authentication_name
)

export const countryCodeSelector = createSelector(
  currentUserSelector,
  (user) => user.country_code
)

export const companyRoleSelector = createSelector(
  currentUserSelector,
  (user) => user.company_role
)

export const timezoneSelector = createSelector(
  currentUserSelector,
  (user) => user.timezone
)

export const dateNotationSelector = createSelector(
  currentUserSelector,
  (user) => user.date_notation
)

export const timeNotationSelector = createSelector(
  currentUserSelector,
  (user) => user.time_notation
)

export const idSelector = createSelector(
  currentUserSelector,
  (user) => user.id
)

export const uuidSelector = createSelector(
  currentUserSelector,
  (user) => user.uuid
)

export const orgLogoUrlSelector = createSelector(
  currentUserSelector,
  ({ org_logo_url: orgLogoUrl }) => orgLogoUrl
)

export const localeSelector = createSelector(
  currentUserSelector,
  (user) => user && user.locale
)


const isGotomeetingConfigured = createSelector(currentUserSelector, (currentUser) => currentUser.is_gotomeeting_configured)
const isZoomConfigured = createSelector(currentUserSelector, (currentUser) => currentUser.is_zoom_configured)
const isMicrosoftTeamsConfigured = createSelector(currentUserSelector, (currentUser) => currentUser.is_microsoft_teams_configured)
const googleConferencingConfigured = createSelector(currentUserSelector, (currentUser) => currentUser.google_conferencing_configured)
const isWebexConfigured = createSelector(currentUserSelector, (currentUser) => currentUser.is_webex_configured)

export const googleConferencingConfiguredSelector = googleConferencingConfigured

const integrationsSelector = createSelector(
  isGotomeetingConfigured,
  isZoomConfigured,
  isMicrosoftTeamsConfigured,
  googleConferencingConfigured,
  isWebexConfigured,
  (isGotomeetingEnabled, isZoomEnabled, isMicrosoftTeamsEnabled, googleConferencingEnabled, isWebexEnabled) => (
    {
      gotomeeting_conference: isGotomeetingEnabled,
      zoom_conference: isZoomEnabled,
      microsoft_teams_conference: isMicrosoftTeamsEnabled,
      google_conference: googleConferencingEnabled,
      webex_conference: isWebexEnabled
    }
  )
)

export const enabledIntegrationsSelector = createSelector(
  integrationsSelector,
  (integrations) => Object.entries(integrations).reduce((acc, [integration, isEnabled]) => {
    if (isEnabled) {
      return [...acc, integration]
    }
    return acc
  }, [])
)

export const roleSelector = createSelector(
  currentUserSelector,
  ({ organization_membership: { role } = {} }) => role
)

const isAdminSelector = createSelector(
  roleSelector,
  (role) => role === 'admin'
)

export const isOwnerSelector = createSelector(
  roleSelector,
  (role) => role === 'owner'
)

export const isAdminOrOwnerSelector = createSelector(
  isAdminSelector,
  isOwnerSelector,
  (isAdmin, isOwner) => (
    isAdmin || isOwner
  )
)

export const emailSelector = createSelector(
  currentUserSelector,
  (user) => user.email
)

export const userGroupRelationshipsSelector = createSelector(
  currentUserSelector,
  (user) => user.user_group_relationships
)

export const groupsManagedByUserSelector = createSelector(
  userGroupRelationshipsSelector,
  (userGroupRelationships) => Object.values(userGroupRelationships)
    .filter((relationship) => !!relationship.admin)
    .map((relationship) => relationship.admin)
)

export const isGroupAdminSelector = createSelector(
  currentUserSelector,
  (user) => user.is_user_group_admin
)

export const isOnlyGroupAdminSelector = createSelector(
  isAdminOrOwnerSelector,
  isGroupAdminSelector,
  (isAdminOrOwner, isGroupAdmin) => !isAdminOrOwner && isGroupAdmin
)

export const isAdminOrOwnerOrGroupAdminSelector = createSelector(
  isAdminOrOwnerSelector,
  isGroupAdminSelector,
  (isAdminOrOwner, isGroupAdmin) => isAdminOrOwner || isGroupAdmin
)

export const isBrandedSelector = createSelector(
  currentUserSelector,
  ({ is_branded: isBranded }) => isBranded
)

export const eventsCountSelector = createSelector(
  currentUserSelector,
  ({ events_count: eventsCount }) => eventsCount
)

export const createdAtSelector = createSelector(
  currentUserSelector,
  ({ created_at: createdAt }) => createdAt
)
